import type { ReleaseEntry } from './types';

/** Where changelog entries come from. Abstracted so the backend swap is drop-in. */
export interface ContentSource {
  /** Static hosted changelog page from the list response, when available. */
  readonly staticPageUrl?: string;
  /** Return entries, newest first. */
  list(): Promise<ReleaseEntry[]>;
  /** Fetch the full body of a single entry by id (used to expand a summarized list item). */
  get(id: string): Promise<ReleaseEntry>;
}

function sortNewestFirst(entries: ReleaseEntry[]): ReleaseEntry[] {
  return entries.slice().sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/** Serves a fixed in-memory list. Backs the bundled dummy data (dev / no-backend fallback). */
export class StaticContentSource implements ContentSource {
  #entries: ReleaseEntry[];

  constructor(entries: ReleaseEntry[]) {
    this.#entries = sortNewestFirst(entries);
  }

  list(): Promise<ReleaseEntry[]> {
    return Promise.resolve(this.#entries.slice());
  }

  get(id: string): Promise<ReleaseEntry> {
    const found = this.#entries.find(entry => entry.id === id);
    return found ? Promise.resolve(found) : Promise.reject(new Error(`Unknown entry: ${id}`));
  }
}

// ---- Backend wire format: GET /public-client/v1/release-notes[ /{id} ] ----

/** One localized body of a release note, as returned by the public-client endpoints. */
interface ReleaseNoteContentDto {
  language: string;
  isDefaultLanguage: boolean;
  title: string;
  /** Full sanitizable HTML, or a plain-text summary when the list was fetched with summaryLength. */
  content: string;
  summarized: boolean;
  /** Size of the full HTML body — lets us tell whether a summary was actually truncated. */
  fullContentLength: number;
}

/** A published release note, as returned by the public-client endpoints. */
interface ReleaseNoteDto {
  id: string;
  name: string;
  /** "feature" | "improvement" | "fix" | null. */
  type: string | null;
  tags: string[] | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  contents: ReleaseNoteContentDto[];
}

/** Envelope of the list endpoint. */
interface ReleaseNotesListDto {
  appId: string;
  timeframeDays: number;
  staticPageUrl?: string | null;
  releaseNotes: ReleaseNoteDto[];
}

/**
 * Pick the content to show for a note: the requested language if present, else the note's
 * default-language content, else the first available. The backend already applies this fallback
 * when a `language` query param is sent, but we repeat it so the mapper is correct either way.
 */
function pickContent(contents: ReleaseNoteContentDto[], language?: string): ReleaseNoteContentDto | undefined {
  if (!contents || contents.length === 0) return undefined;
  if (language) {
    const match = contents.find(c => c.language?.toLowerCase() === language.toLowerCase());
    if (match) return match;
  }
  return contents.find(c => c.isDefaultLanguage) ?? contents[0];
}

function normalizeStaticPageUrl(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

/** Map a backend release note onto the SDK's flat, single-language `ReleaseEntry`. */
export function mapReleaseNote(note: ReleaseNoteDto, language?: string): ReleaseEntry {
  const content = pickContent(note.contents ?? [], language);
  const body = content?.content ?? '';
  const fullLength = typeof content?.fullContentLength === 'number' ? content.fullContentLength : body.length;
  return {
    id: note.id,
    title: content?.title ?? note.name ?? '',
    html: body,
    // publishedAt drives ordering + unread; every returned note is published, but fall back defensively.
    publishedAt: note.publishedAt ?? note.createdAt ?? '',
    tags: note.tags && note.tags.length > 0 ? note.tags : undefined,
    category: note.type ?? undefined,
    // Only offer "Show more" when the summary genuinely dropped content.
    truncated: content?.summarized === true && fullLength > body.length,
  };
}

/** Options for talking to the ClickRelease public-client API. */
export interface RemoteSourceOptions {
  /** Backend origin, e.g. `https://cr-backend.clickterm.net`. Trailing slashes are trimmed. */
  apiBase: string;
  /** The app / project identifier issued by ClickRelease. Sent as the `appId` query param. */
  appId: string;
  /** Restrict content to a single language code (e.g. `en`). Omit to receive all languages. */
  language?: string;
  /** Lookback window in days for the list endpoint. Omit to use the server default (7). */
  timeframeDays?: number;
  /** When > 0, the list returns plain-text summaries of this length; full bodies load on demand. */
  summaryLength?: number;
}

/**
 * Fetches release notes from the ClickRelease public-client API. `list()` hits
 * `GET /public-client/v1/release-notes` (optionally summarized); `get(id)` hits
 * `GET /public-client/v1/release-notes/{id}` for the full HTML body. No auth / API key.
 */
export class RemoteContentSource implements ContentSource {
  #base: string;
  #opts: RemoteSourceOptions;
  #fullCache = new Map<string, ReleaseEntry>();
  #staticPageUrl: string | undefined;

  constructor(opts: RemoteSourceOptions) {
    this.#opts = opts;
    this.#base = opts.apiBase.replace(/\/+$/, '');
  }

  get staticPageUrl(): string | undefined {
    return this.#staticPageUrl;
  }

  async list(signal?: AbortSignal): Promise<ReleaseEntry[]> {
    const params = new URLSearchParams({ appId: this.#opts.appId });
    if (this.#opts.timeframeDays && this.#opts.timeframeDays > 0) {
      params.set('timeframeDays', String(this.#opts.timeframeDays));
    }
    if (this.#opts.summaryLength && this.#opts.summaryLength > 0) {
      params.set('summaryLength', String(this.#opts.summaryLength));
    }
    if (this.#opts.language) params.set('language', this.#opts.language);

    const data = await this.#fetch<ReleaseNotesListDto>(`/public-client/v1/release-notes?${params}`, signal);
    this.#staticPageUrl = normalizeStaticPageUrl(data.staticPageUrl);
    const notes = Array.isArray(data.releaseNotes) ? data.releaseNotes : [];
    return sortNewestFirst(notes.map(note => mapReleaseNote(note, this.#opts.language)));
  }

  async get(id: string, signal?: AbortSignal): Promise<ReleaseEntry> {
    const cached = this.#fullCache.get(id);
    if (cached) return cached;

    const params = new URLSearchParams({ appId: this.#opts.appId });
    if (this.#opts.language) params.set('language', this.#opts.language);

    const note = await this.#fetch<ReleaseNoteDto>(`/public-client/v1/release-notes/${encodeURIComponent(id)}?${params}`, signal);
    const entry = mapReleaseNote(note, this.#opts.language);
    this.#fullCache.set(id, entry);
    return entry;
  }

  async #fetch<T>(path: string, signal?: AbortSignal): Promise<T> {
    const res = await fetch(`${this.#base}${path}`, { signal, headers: { accept: 'application/json' } });
    if (!res.ok) {
      throw new Error(`ClickRelease request failed (${res.status}) for ${path}`);
    }
    return (await res.json()) as T;
  }
}
